import { api } from "@/api";
import { MutationConfig, MutationResult } from "@/shared/types/base";
import { CajaMovimiento } from "@/shared/types/caja-movimiento";
import { Transferencia, TransferenciaRequest } from "@/shared/types/transferencia";
import { Venta } from "@/shared/types/venta";
import { sleep } from "@/shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";

export type Pago = {
    fecha: string,
    venta: Venta,
    pago: Transferencia,
    vuelto: Transferencia,
    movimiento: CajaMovimiento
}

export interface PayVentaRequest {
    pago: TransferenciaRequest,
    vuelto: TransferenciaRequest,
    saltarFE?: boolean
}

export async function payVenta(ventaId: string, request: PayVentaRequest): Promise<Pago> {
    await sleep(1000);
    const response = await api.post("caja_actual/pay/" + ventaId, request);
    return response.data;
}

export function usePayVentaMutation(ventaId: string, mutationConfig: MutationConfig<Pago, PayVentaRequest> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: request => payVenta(ventaId, request),
        onSuccess: (...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(...args);
            queryClient.invalidateQueries({ queryKey: ["ventas", "unpaid"] });
        },
    })
}

export interface PayVentaMutationProps {
    ventaId: string,
    mutationConfig?: MutationConfig<Pago, PayVentaRequest>
    children?: (mutationResult: MutationResult<Pago, PayVentaRequest>) => ReactNode
}

export function PayVentaMutation({
    ventaId,
    mutationConfig,
    children
}: PayVentaMutationProps) {
    const mutationResult = usePayVentaMutation(ventaId, mutationConfig);
    return children && children(mutationResult);
}